// src/modules/app-views/services/pages/Document/tabs/ParametersTab/components/ProcedureStepsCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback } from 'react';
import { Card, Table, Button, Flex, Space, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { servicesAddToSet } from '../../../../../service';
import ProcedureStepModal from '../modals/ProcedureStepModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ProcedureStepsCard = ({ data, disabled, onRefresh }) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const dataSource = data?.procedureSteps || [];

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [loading, setLoading] = useState(false);

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleAdd = useCallback(() => {
    setEditingRecord(null);
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback(record => {
    setEditingRecord(record);
    setModalOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setModalOpen(false);
    setEditingRecord(null);
  }, []);

  const handleSave = useCallback(
    async formData => {
      setLoading(true);
      try {
        const payload = editingRecord
          ? { _id: editingRecord._id, ...formData }
          : formData;

        await servicesAddToSet({
          _id: data._id,
          operator: '$push',
          path: 'procedureSteps',
          data: payload,
        });

        appService.message({ flag: 's', text: 'saved_successfully' });
        setModalOpen(false);
        setEditingRecord(null);
        onRefresh?.();
      } catch (error) {
        console.error('[ProcedureStepsCard] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [data?._id, editingRecord, onRefresh]
  );

  const handleDelete = useCallback(
    async record => {
      setLoading(true);
      try {
        await servicesAddToSet({
          _id: data._id,
          operator: '$pull',
          path: 'procedureSteps._id',
          data: { _id: record._id },
        });

        appService.message({ flag: 's', text: 'removed_successfully' });
        onRefresh?.();
      } catch (error) {
        console.error('[ProcedureStepsCard] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [data?._id, onRefresh]
  );

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  const columns = buildColumns({ disabled, loading, handleEdit, handleDelete });

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <>
      <Card title={<div className="text-muted">{translateX('step_by_step')}</div>}>
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          size="small"
          loading={loading}
          locale={{ emptyText: translateX('no_data') }}
        />

        {!disabled && (
          <Flex justify="flex-end" className="mt-3">
            <Button
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={loading}
            >
              {translateX('add_step')}
            </Button>
          </Flex>
        )}
      </Card>

      <ProcedureStepModal
        open={modalOpen}
        onCancel={handleCancel}
        onSave={handleSave}
        initialData={editingRecord}
        loading={loading}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ProcedureStepsCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildColumns({ disabled, loading, handleEdit, handleDelete }) {
  const cols = [
    {
      title: translateX('phase'),
      dataIndex: 'phase',
      width: 150,
      ellipsis: true,
      render: text => <strong>{text}</strong>,
    },
    {
      title: translateX('duration'),
      dataIndex: 'duration',
      width: 100,
      render: text =>
        text ? (
          <span className="text-muted">{text}</span>
        ) : (
          <span className="text-muted">-</span>
        ),
    },
    {
      title: translateX('description'),
      dataIndex: 'description',
      ellipsis: true,
      render: text =>
        text ? (
          <span className="text-muted">{text}</span>
        ) : (
          <span className="text-muted">-</span>
        ),
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 110,
      align: 'center',
      render: (_, record) => {
        const count = record.actions?.length || 0;
        return (
          <Tag color={count > 0 ? 'default' : undefined}>
            {count} {count === 1 ? translateX('action') : translateX('actions')}
          </Tag>
        );
      },
    },
  ];

  // edit/delete column only when not disabled
  if (!disabled) {
    cols.push({
      key: 'operations',
      width: 90,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={loading}
            />
          </Popconfirm>
        </Space>
      ),
    });
  }

  return cols;
}
